import React from 'react';
import {useParams} from 'react-router';
import {useLocalization} from 'localization';
import type {LinkedLiterary} from 'schemaDefinition/types/values';
import {useGetSeriesQuery} from 'services/series/seriesApi';
import {ActivityIndicator, FlexBox, Scene} from 'components';
import {DataFormV1} from 'schema';
import {useSceneExtensionsContext} from 'sceneExtensions';
import {CompareSeries} from './components/CompareSeries';

export const MergeSeriesSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {currentExtension} = useSceneExtensionsContext();
  const {seriesId: seriesIdParam, otherId: otherIdParam} = useParams();

  const {data: seriesData, isLoading: seriesIsLoading} = useGetSeriesQuery({
    id: seriesIdParam || '',
  });
  const {data: otherData, isLoading: otherIsLoading} = useGetSeriesQuery({
    id: otherIdParam || '',
  });

  const isLoading = seriesIsLoading || otherIsLoading;

  const linkedLiterarySeries: LinkedLiterary = seriesData
    ? {
        link: {
          linkStatus: 'verified',
          entityId: seriesData.id,
          type: seriesData.collectionType,
          name: seriesData.titles?.[0].value || '<uten tittel>',
        },
        role: 'partOf.work',
      }
    : {
        link: {
          linkStatus: 'empty',
        },
      };
  const linkedLiteraryOther: LinkedLiterary = otherData
    ? {
        link: {
          linkStatus: 'verified',
          entityId: otherData.id,
          type: otherData.collectionType,
          name: otherData.titles?.[0].value || '<uten tittel>',
        },
        role: 'partOf.work',
      }
    : {
        link: {
          linkStatus: 'empty',
        },
      };

  return (
    <Scene
      browserTitle={t('appbar.series')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={'Seriesammenslåing'} />
      <Scene.Content>
        {isLoading ? (
          <ActivityIndicator />
        ) : seriesData && otherData ? (
          <>
            <FlexBox left horizontal gap={2} marginBottom={4} width={'800px'}>
              <DataFormV1
                id={'mergedSeriesData'}
                schema={{
                  name: 'mergedSeriesData',
                  parts: [
                    {
                      type: 'linkedLiterary',
                      name: 'seriesSource',
                      key: 'seriesSource',
                      cardinality: 'single',
                      roleCodelistId: 'series.linkedRole',
                      linkRole: 'partOf.work',
                    },
                    {
                      type: 'linkedLiterary',
                      name: 'seriesTarget',
                      key: 'seriesTarget',
                      cardinality: 'single',
                      roleCodelistId: 'series.linkedRole',
                      linkRole: 'partOf.work',
                    },
                  ],
                }}
                data={{
                  seriesSource: linkedLiterarySeries,
                  seriesTarget: linkedLiteraryOther,
                }}
                configuration={{
                  schemaContainers: {
                    defaultContainer: 'DefaultCard',
                  },
                  parts: {
                    seriesSource: {
                      layout: {
                        flex: 1,
                      },
                    },
                    seriesTarget: {
                      layout: {
                        flex: 1,
                      },
                    },
                  },
                }}
                mode="read-only"
                relatedData={{}}
                showErrors={false}
                onDataChanged={() => undefined}
                setApprovedValue={() => undefined}
              />
            </FlexBox>
            <CompareSeries seriesId={seriesData.id} otherId={otherData.id} />
          </>
        ) : (
          <div>Ugyldig serie IDer.</div>
        )}
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
